import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'semantic-ui-react';

/**
 * CustomButton - A custom replacement for Semantic UI's Button component
 * Maintains similar API but uses custom styling
 */
const CustomButton = ({
  color,
  size = 'medium',
  icon,
  iconPosition = 'left',
  primary = false,
  secondary = false,
  basic = false,
  disabled = false,
  loading = false,
  fluid = false,
  circular = false,
  onClick,
  type = 'button',
  children,
  className = '',
  style = {},
  ...otherProps
}) => {
  // Color mapping (matches Semantic UI colors)
  const getBackgroundColor = (color) => {
    const colorMap = {
      'red': '#db2828',
      'orange': '#f2711c',
      'yellow': '#fbbd08',
      'olive': '#b5cc18',
      'green': '#21ba45',
      'teal': '#00b5ad',
      'blue': '#2185d0',
      'violet': '#6435c9',
      'purple': '#a333c8',
      'pink': '#e03997',
      'brown': '#a5673f',
      'grey': '#767676',
      'black': '#1b1c1d'
    };
    return colorMap[color] || '#e0e1e2'; // Default to grey
  };

  // Get hover color (slightly darker)
  const getHoverColor = (color) => {
    const hoverMap = {
      'red': '#ca1010',
      'orange': '#e55100',
      'yellow': '#eaaa00',
      'olive': '#a7bd0d',
      'green': '#16ab39',
      'teal': '#009c95',
      'blue': '#1678c2',
      'violet': '#5829bb',
      'purple': '#9627ba',
      'pink': '#d41c84',
      'brown': '#975b33',
      'grey': '#5d5d5d',
      'black': '#0f0f10'
    };
    return hoverMap[color] || '#cacbcd';
  };

  // Size mapping for font and padding
  const getSizeStyles = (size) => {
    const sizeMap = {
      'mini': {
        fontSize: '0.78571429rem',
        padding: '0.58571429em 0.58571429em'
      },
      'tiny': {
        fontSize: '0.85714286rem',
        padding: '0.64285714em 0.64285714em'
      },
      'small': {
        fontSize: '0.92857143rem',
        padding: '0.67857143em 0.67857143em'
      },
      'medium': {
        fontSize: '1rem',
        padding: '0.78571429em 1.5em'
      },
      'large': {
        fontSize: '1.14285714rem',
        padding: '0.85714286em 1.5em'
      },
      'big': {
        fontSize: '1.28571429rem',
        padding: '0.92857143em 1.5em'
      },
      'huge': {
        fontSize: '1.42857143rem',
        padding: '1em 1.5em'
      },
      'massive': {
        fontSize: '1.71428571rem',
        padding: '1.14285714em 1.5em'
      }
    };
    return sizeMap[size] || sizeMap['medium'];
  };

  // Determine text color based on button style
  const getTextColor = () => {
    if (basic) {
      return color ? getBackgroundColor(color) : '#000000';
    }
    // For yellow/olive backgrounds, use dark text
    if (!basic && (color === 'yellow' || color === 'olive')) {
      return 'rgba(0,0,0,.85)';
    }
    return '#FFFFFF';
  };

  // Build button styles
  const buildButtonStyles = () => {
    const sizeStyles = getSizeStyles(size);
    const bgColor = color ? getBackgroundColor(color) : '#e0e1e2';

    let buttonStyles = {
      display: 'inline-block',
      minHeight: '1em',
      outline: '0',
      border: 'none',
      verticalAlign: 'baseline',
      fontFamily: '"Lato", "Helvetica Neue", Arial, Helvetica, sans-serif',
      fontWeight: '700',
      lineHeight: '1em',
      textAlign: 'center',
      textDecoration: 'none',
      borderRadius: circular ? '5rem' : '0.28571429rem',
      cursor: disabled ? 'not-allowed' : 'pointer',
      userSelect: 'none',
      transition: 'opacity 0.1s ease, background-color 0.1s ease, color 0.1s ease, box-shadow 0.1s ease, background 0.1s ease',
      willChange: 'auto',
      WebkitTapHighlightColor: 'transparent',
      fontSize: sizeStyles.fontSize,
      padding: sizeStyles.padding,
      ...(fluid && { width: '100%', display: 'block' })
    };

    // Apply background and text colors based on style
    if (basic) {
      buttonStyles = {
        ...buttonStyles,
        backgroundColor: 'transparent',
        color: bgColor,
        border: `1px solid ${bgColor}`,
        boxShadow: 'none'
      };
    } else {
      buttonStyles = {
        ...buttonStyles,
        backgroundColor: bgColor,
        color: getTextColor(),
        boxShadow: '0 0 0 1px transparent inset, 0 0 0 0 rgba(34,36,38,.15) inset'
      };
    }

    // Apply disabled styles
    if (disabled) {
      buttonStyles = {
        ...buttonStyles,
        opacity: '0.45',
        backgroundColor: basic ? 'transparent' : '#e0e1e2',
        color: basic ? 'rgba(0,0,0,.3)' : 'rgba(0,0,0,.6)',
        pointerEvents: 'none'
      };
    }

    // Apply loading styles
    if (loading) {
      buttonStyles = {
        ...buttonStyles,
        position: 'relative',
        cursor: 'default',
        pointerEvents: 'none',
        opacity: '0.65'
      };
    }

    return buttonStyles;
  };

  // Handle mouse enter for hover effect
  const handleMouseEnter = (e) => {
    if (!disabled && !loading && !basic) {
      e.target.style.backgroundColor = getHoverColor(color);
    } else if (!disabled && !loading && basic) {
      e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
    }
  };

  // Handle mouse leave to reset color
  const handleMouseLeave = (e) => {
    if (!disabled && !loading) {
      if (basic) {
        e.target.style.backgroundColor = 'transparent';
      } else {
        e.target.style.backgroundColor = color ? getBackgroundColor(color) : '#e0e1e2';
      }
    }
  };

  // Handle mouse down for active effect
  const handleMouseDown = (e) => {
    if (!disabled && !loading) {
      e.target.style.transform = 'scale(0.98)';
    }
  };

  // Handle mouse up to reset scale
  const handleMouseUp = (e) => {
    if (!disabled && !loading) {
      e.target.style.transform = 'scale(1)';
    }
  };

  // Detect whether the icon is an SVG path or a Semantic UI icon name
  // SVG paths contain '/' or '.' (e.g., '/static/media/arrow-left.svg' or imported paths)
  // Semantic UI icon names are simple strings like 'eye', 'paper plane', 'check', 'users'
  const isSvgIcon = icon && (icon.includes('/') || icon.includes('.'));
  const isSemanticIcon = icon && !isSvgIcon;

  // Render the appropriate icon element
  const renderIcon = (position) => {
    if (!icon || iconPosition !== position || loading) return null;

    if (isSvgIcon) {
      const imgStyle = position === 'left'
        ? {
          height: '16px',
          width: '16px',
          display: 'inline-block',
          verticalAlign: 'middle',
          marginRight: children ? '0.5em' : 0,
          marginLeft: '-0.25em'
        }
        : {
          height: '22px',
          width: '22px',
          display: 'inline-block',
          verticalAlign: 'middle',
          marginLeft: children ? '0.5em' : 0,
          marginRight: '-0.25em'
        };

      return <img src={icon} alt="" style={imgStyle} />;
    }

    if (isSemanticIcon) {
      return (
        <Icon
          name={icon}
          style={{
            margin: 0,
            marginRight: position === 'left' && children ? '0.4em' : 0,
            marginLeft: position === 'right' && children ? '0.4em' : 0,
            opacity: 1
          }}
        />
      );
    }

    return null;
  };

  return (
    <button
      type={type}
      className={`custom-button custom-button-${color} custom-button-${size} ${primary ? 'primary' : ''} ${secondary ? 'secondary' : ''} ${basic ? 'basic' : ''} ${className}`}
      style={{
        ...buildButtonStyles(),
        ...style
      }}
      onClick={disabled || loading ? undefined : onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      disabled={disabled}
      {...otherProps}
    >
      {/* Loading spinner */}
      {loading && (
        <span style={{
          display: 'inline-block',
          marginRight: '0.5em',
          animation: 'spin 0.6s linear infinite'
        }}>
          ⟳
        </span>
      )}

      {/* Left icon */}
      {renderIcon('left')}

      {/* Button text */}
      {children && <span style={{ verticalAlign: 'middle' }}>{children}</span>}

      {/* Right icon */}
      {renderIcon('right')}

      {/* Add CSS for loading spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
};

CustomButton.propTypes = {
  color: PropTypes.string,
  size: PropTypes.oneOf(['mini', 'tiny', 'small', 'medium', 'large', 'big', 'huge', 'massive']),
  icon: PropTypes.string, // SVG import path OR Semantic UI icon name (e.g., 'eye', 'paper plane')
  iconPosition: PropTypes.oneOf(['left', 'right']),
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  basic: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fluid: PropTypes.bool,
  circular: PropTypes.bool,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object
};

export default CustomButton;